import type { HeroRoom, Role } from "./types";
import { SALAS_KIOSCO } from "./lib/salas";

// Configuración del programa y catálogos estáticos.
//
// ⚠️ AQUÍ NO VA NINGUNA SEDE. Esto describía "Residencial Quintana" con un
// `siteCode: "QUINTANA-SJ"` que NO EXISTE en la base de datos (la migración 010
// sembró los 117 residenciales reales de AVP con códigos `avp-<municipio>-<nombre>`).
// El panel enseñaba ese nombre a todo el mundo —incluido quien administra los 122—
// y peor: el alta de participantes y el radicado de solicitudes mandaban ese código
// al backend, que respondía `400 Sede desconocida` y el frontend se lo tragaba en
// silencio. Nada se guardaba.
//
// La sede sale ahora del token (`GET /sites`, lib/sites.ts). Lo que queda aquí es
// lo que de verdad es común a todos los residenciales: el nombre del programa y la
// agencia que los administra.

// Aquí vivía también `lessons: 79`, que el hero enseñaba en TODAS las salas.
// Nunca fue cierto de ninguna —Elemental sola pasa de mil— y era un número que
// había que acordarse de subir a mano cada vez que entraba contenido. Ahora cada
// sala cuenta las suyas (ver RoomPage → `totalLecciones`).
export const PROGRAMA = {
  program: "Centro Educativo Tecnológico",
  agency: "Administración de Vivienda Pública",
  tagline: "Capacitación y desarrollo personal para nuestros residentes",
  subtitle:
    "Acceso a computadoras, Internet y servicios electrónicos internos. Ofrecemos talleres y atención individualizada para optimizar las destrezas en computadoras, y ayuda para realizar las tareas escolares.",
};

// La app KidsIQ Biblioteca para iPhone y iPad. Es Unlisted: no sale en las
// búsquedas de la App Store, así que este enlace es la única forma de llegar a
// ella. El id es también el que usa el banner de Safari en index.html.
export const APP_IOS = {
  id: "6797998081",
  url: "https://apps.apple.com/us/app/kidsiq-biblioteca/id6797998081",
};

// Endpoints (sobreescribibles por variables de entorno de Vite).
export const API_BASE =
  import.meta.env.VITE_API_BASE ??
  "https://eorzyez0nd.execute-api.us-east-1.amazonaws.com";

// Offline-first: el contenido se sirve local (copiado en /public/content).
// Se puede apuntar al CDN con VITE_CONTENT_BASE.
export const CONTENT_BASE = import.meta.env.VITE_CONTENT_BASE ?? "/content";

// Navegación de las "salas".
export interface NavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  soon?: boolean;
}

// Navegación del estudiante: las salas de aprendizaje y servicios.
export const STUDENT_NAV: NavItem[] = [
  { key: "servicios", label: "Servicios a la Ciudadanía", icon: "Landmark", path: "/servicios" },
  { key: "elemental", label: "Elemental", icon: "BookOpen", path: "/elemental" },
  { key: "intermedia", label: "Intermedia", icon: "Library", path: "/intermedia" },
  { key: "superior", label: "Superior", icon: "GraduationCap", path: "/superior" },
  { key: "refuerzo", label: "Catálogo por grado", icon: "ClipboardList", path: "/refuerzo" },
  { key: "universidad", label: "Camino a la Universidad", icon: "School", path: "/universidad" },
  { key: "stem", label: "STEM y Juegos", icon: "Puzzle", path: "/stem" },
  { key: "ia", label: "Inteligencia Artificial", icon: "Sparkles", path: "/ia" },
  { key: "videoteca", label: "Videoteca", icon: "Youtube", path: "/videoteca" },
  { key: "deportes", label: "Videoteca Deportes", icon: "Trophy", path: "/deportes" },
  { key: "eventos", label: "Eventos", icon: "CalendarDays", path: "/eventos" },
  // "Lectura para la vida": la entrada se añade condicionalmente abajo (ALFA_EN_MENU),
  // para no mostrar el módulo hasta que la unidad esté grabada (audios + imágenes). La
  // ruta /alfabetizacion existe igual, así que se puede probar por URL.
  { key: "perfil", label: "Mi perfil", icon: "UserCircle", path: "/perfil" },
];

// Solicitudes (tickets a agencias): apagado para el lanzamiento (2026-09-28, a pedido
// del usuario: no se va a usar). Sin menú, sin ruta, sin sección en el panel y fuera
// de los editores de acceso. El backend y la tabla quedan intactos pero sin pantalla;
// esta constante es la única llave para volver a encenderlo.
export const SOLICITUDES_EN_MENU = false;
if (SOLICITUDES_EN_MENU) {
  STUDENT_NAV.splice(STUDENT_NAV.length - 1, 0, {
    key: "adultos", label: "Solicitudes", icon: "Users", path: "/adultos",
  });
}
/** Salas que existen en el catálogo del backend pero no se ofrecen en ninguna pantalla. */
export const SALAS_OCULTAS: readonly string[] = SOLICITUDES_EN_MENU ? [] : ["adultos"];

// Poner en `true` cuando los assets de la unidad piloto estén completos y la compuerta
// de validación pase. Hasta entonces el módulo no aparece en el menú.
export const ALFA_EN_MENU = false;
if (ALFA_EN_MENU) {
  STUDENT_NAV.splice(STUDENT_NAV.length - 1, 0, {
    key: "alfabetizacion", label: "Lectura para la vida", icon: "BookOpen", path: "/alfabetizacion",
  });
}

// Educación Financiera: un solo flag gobierna menú, enlace de Adultos y acceso.
// Encendido a pedido del usuario (2026-08-07). Nota: el contenido sigue en
// `pending-review` (borradores con fuentes; sin verificación de especialista aún).
export const FINANZAS_EN_MENU = true;
if (FINANZAS_EN_MENU) {
  STUDENT_NAV.splice(STUDENT_NAV.length - 1, 0, {
    key: "finanzas", label: "Educación Financiera", icon: "PieChart", path: "/finanzas",
  });
}

// Currículo de Educación Financiera POR GRADO (Mi Futuro Seguro). Cuando es `true`, el
// selector de grado en /finanzas muestra los grados (Kínder…) a todos; con `false`, solo
// el staff/demo los ve (modo revisión). Encendido a pedido del usuario (2026-08-07);
// contenido alineado a estándares DEPR, sin revisión de educador línea por línea. Reversible.
export const FINANZAS_GRADOS_EN_MENU = true;

// Módulo de Empleo (sala OPT-IN). El flag controla el menú/exposición; el acceso real
// es por usuario (allowed_rooms), y como `empleo` es opt-in, NUNCA se concede por NULL:
// hay que otorgarlo explícito (Adultos/Superior). Apagado hasta QA.
export const EMPLEO_EN_MENU = true;
if (EMPLEO_EN_MENU) {
  STUDENT_NAV.splice(STUDENT_NAV.length - 1, 0, {
    key: "empleo", label: "Empleo", icon: "Briefcase", path: "/empleo",
  });
}

// Firma institucional de la gobernadora, al pie del login y al pie del sidebar.
//
// Va detrás de un flag y no cableada en los componentes A PROPÓSITO: una firma
// personal es de la administración de turno, no de la plataforma. El día que
// cambie, esto se apaga en una línea y no hay que ir a cazarla por tres archivos.
//
// El componente `FirmaGobernadora` consulta este flag por su cuenta: quien la
// pinta no tiene que acordarse de comprobarlo, y así `false` significa de verdad
// "no queda rastro en ninguna pantalla".
export const FIRMA_GOBERNADORA = true;

// Motivos de visita: ahora son la lista editable `visita_motivo` (lib/catalogos.ts).

// Área de administración: cada sección es una página con su propia ruta (/admin/X),
// filtrada por el rol REAL de Cognito. Sustituye a las pestañas dentro de un panel.
export interface AdminNavItem extends NavItem {
  roles: string[]; // qué cognitoRole ve la sección
}
const ALL_STAFF = ["super_admin", "admin", "agent_admin", "librarian"];
// Quien administra más de un residencial necesita verlos. Una bibliotecaria no:
// su alcance es una sede, y una "lista" de un elemento no informa de nada. El
// agente administrativo sí: administra los de su compañía, hasta 36.
const MULTI_SEDE = ["super_admin", "admin", "agent_admin"];
export const ADMIN_SECTIONS: AdminNavItem[] = [
  { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard", path: "/admin/dashboard", roles: ALL_STAFF },
  { key: "residenciales", label: "Residenciales", icon: "Building2", path: "/admin/residenciales", roles: MULTI_SEDE },
  { key: "usuarios", label: "Usuarios", icon: "Users", path: "/admin/usuarios", roles: ALL_STAFF },
  ...(SOLICITUDES_EN_MENU
    ? [{ key: "solicitudes", label: "Solicitudes", icon: "Inbox", path: "/admin/solicitudes", roles: ALL_STAFF }]
    : []),
  { key: "visitas", label: "Visitas", icon: "Eye", path: "/admin/visitas", roles: ALL_STAFF },
  { key: "empleos", label: "Empleos", icon: "Briefcase", path: "/admin/empleos", roles: ALL_STAFF },
  { key: "eventos", label: "Eventos", icon: "CalendarDays", path: "/admin/eventos", roles: ALL_STAFF },
  { key: "mensajes", label: "Mensajes", icon: "Megaphone", path: "/admin/mensajes", roles: ALL_STAFF },
  { key: "curriculo", label: "Currículo DEPR", icon: "BookOpenCheck", path: "/admin/curriculo", roles: ALL_STAFF },
  { key: "modulos", label: "Módulos", icon: "ToggleLeft", path: "/admin/modulos", roles: ALL_STAFF },
  { key: "plantillas", label: "Plantillas por grado", icon: "GraduationCap", path: "/admin/plantillas", roles: ["super_admin"] },
  // Agencias es SOLO super_admin: el catalogo es global (sin org_id), asi que un
  // cambio lo ven los 122 residenciales. `admin` es AVP, el cliente; su edicion
  // afectaria a toda la red. Cerrado tambien en el backend, no solo aqui.
  { key: "agencias", label: "Agencias", icon: "Landmark", path: "/admin/agencias", roles: ["super_admin"] },
  // Licencias es SOLO super_admin: `admin` es AVP, el cliente al que se le factura.
  // Enseñarle la brecha es enseñarle la factura antes de emitirla (diseño §2).
  { key: "licencias", label: "Licencias", icon: "ShieldCheck", path: "/admin/licencias", roles: ["super_admin"] },
  // Listas de los desplegables y compañías: SOLO super_admin (decisión de LOFA,
  // 2026-10-29). Un cambio aquí lo ve toda la red.
  { key: "listas", label: "Listas", icon: "ListChecks", path: "/admin/listas", roles: ["super_admin"] },
  // Personal lo ve tambien el admin, pero NO ve lo mismo: el servidor le devuelve
  // solo las bibliotecarias de su organizacion. Puede mantener a quien crea
  // —contrasena, invitacion, suspender, mover de residencial— y nada mas: ni al
  // super_admin ni a sus pares. Crear sin poder mantener no es delegar.
  // El agente también entra: mantiene a las bibliotecarias de SUS residenciales
  // (contraseña, invitación, suspender, mover entre ellos). El servidor le
  // devuelve solo esas; aquí no se decide nada, solo se ofrece la puerta.
  { key: "personal", label: "Personal", icon: "UserCog", path: "/admin/personal", roles: ["super_admin", "admin", "agent_admin"] },
];

export function navFor(role: Role, cognitoRole?: string): NavItem[] {
  /*
   * El kiosco: sus salas fijas (`SALAS_KIOSCO`) y nada del panel. Va ANTES de la rama de
   * personal — si cayera ahí, `ADMIN_SECTIONS` le daría Dashboard y Usuarios.
   */
  if (role === "kiosk") {
    return STUDENT_NAV.filter((i) => (SALAS_KIOSCO as readonly string[]).includes(i.key));
  }
  if (role !== "librarian") return STUDENT_NAV;
  const cr = cognitoRole ?? "librarian";
  const secciones = ADMIN_SECTIONS.filter((s) => s.roles.includes(cr));
  return [...secciones, { key: "preview", label: "Vista previa", icon: "BookOpen", path: "/elemental" }];
}

// Tema por materia para las salas de niños: mascota + color de acento.
// Los colores son saturados a propósito (referencia: education.com,
// e-learningforkids). El animal se escoge para que su color dominante
// concuerde con el acento de la materia.
// Las salas de adultos y de servicios NO usan esto: mantienen la paleta
// institucional de AVP, porque ahí se hacen gestiones de gobierno.
export interface SubjectTheme {
  /** SVG en public/assets/animales/ — mascota de las salas de niños. */
  animal?: string;
  accent: string; // color fuerte (texto, bordes)
  accent2: string; // color claro (degradado de la banda)
}

export const SUBJECT_THEME: Record<string, SubjectTheme> = {
  // --- Elemental: mascota ilustrada ---
  matematicas: { animal: "elefante", accent: "#1d4ed8", accent2: "#60a5fa" },
  espanol: { animal: "panda", accent: "#be123c", accent2: "#fb7185" },
  ciencias: { animal: "cocodrilo", accent: "#15803d", accent2: "#4ade80" },
  ingles: { animal: "buho", accent: "#6d28d9", accent2: "#a78bfa" },
  "estudios-sociales": { animal: "gato", accent: "#c2410c", accent2: "#fdba74" },
  "historia-pr": { animal: "leon", accent: "#a16207", accent2: "#fcd34d" },

  /*
   * --- Intermedia y superior: sin mascota ---
   *
   * Mismo acento por materia que en elemental (matemáticas azul, español rojo,
   * ciencias verde…): un estudiante que sube de nivel reconoce su materia por el
   * color aunque cambie el dibujo. Cambiar la paleta por nivel obligaría a
   * reaprender el código de color por el camino.
   *
   * Sin `animal` a propósito: a un estudiante de 7mo a 12mo las mascotas le
   * infantilizan. La ausencia del campo ES la regla — RoomPage pinta mascota si
   * la hay y, si no, el icono de la materia en grande.
   *
   * Aquí había además un campo `icono` que apuntaba a `iconos-materias/*.png`,
   * un pack plano DISTINTO del que usa el resto de la plataforma: dos idiomas
   * gráficos en la misma rejilla. El icono sale ahora de `iconoDeMateria()`, del
   * mismo set que los de unidad y tipo de paso. Los 11 PNG siguen en
   * `public/assets/iconos-materias/` por si hicieran falta; ya no los lee nadie.
   */
  "matematicas-intermedia": { accent: "#1d4ed8", accent2: "#60a5fa" },
  "espanol-intermedia": { accent: "#be123c", accent2: "#fb7185" },
  "ciencias-intermedia": { accent: "#15803d", accent2: "#4ade80" },
  "ingles-intermedia": { accent: "#6d28d9", accent2: "#a78bfa" },
  "estudios-intermedia": { accent: "#c2410c", accent2: "#fdba74" },
  "historia-pr-intermedia": { accent: "#a16207", accent2: "#fcd34d" },

  "matematicas-superior": { accent: "#1d4ed8", accent2: "#60a5fa" },
  "espanol-superior": { accent: "#be123c", accent2: "#fb7185" },
  "biologia-superior": { accent: "#15803d", accent2: "#4ade80" },
  "ingles-superior": { accent: "#6d28d9", accent2: "#a78bfa" },
  "historia-superior": { accent: "#a16207", accent2: "#fcd34d" },
};

/*
 * Aquí vivía `DOMAIN_ICON`: 47 dominios mapeados a los PNG 3D de
 * `public/assets/iconos3d/`, para pintar un icono en cada fila de la lista de
 * lecciones.
 *
 * Se retira, y conviene saber por qué antes de reponerla. El contenido tiene
 * 104 dominios distintos escritos a mano y la tabla exigía coincidencia EXACTA,
 * así que cubría 418 de 2.409 lecciones —el 17 %— y fallaba por cosas como que
 * el contenido dijera "Numeración y Operación" donde la tabla decía
 * "Numeración y Operaciones". Encima se pintaba con regla de todo-o-nada (o
 * todos los dominios del módulo tienen icono, o no se enseña ninguno), así que
 * en la práctica no se encendía casi nunca.
 *
 * Lo sustituye el icono de la UNIDAD (`lib/iconosIlustrados.ts`), que busca por
 * subcadena y SIEMPRE tiene respaldo —el icono de la materia—, así que no hace
 * falta ninguna regla de todo-o-nada.
 *
 * Los 64 PNG siguen en `public/assets/iconos3d/`: son del estilo 3D con brillo,
 * que le queda bien a las salas de adultos (Empleo, Finanzas, Universidad) y
 * mal al lado de las mascotas. No se borran; se dejan para quien los use ahí.
 */

// Configuración visual de cada sala (para la página de sala reutilizable).
export interface RoomConfig {
  title: string;
  kicker: string;
  subtitle: string;
  heroRoom: HeroRoom;
  icon: string;
  accent: "el" | "stem" | "int" | "sup" | "ia";
  sectionTitle: string;
  sectionMuted: string;
  /** Sala infantil: activa tipografía redondeada y tarjetas con mascota.
   *  Intermedia y Superior (7mo-12mo) quedan fuera a propósito: a un
   *  adolescente las mascotas le resultan infantilizantes. */
  esNinos?: boolean;
  /**
   * Sala de escuela superior: tarjetas sobrias.
   *
   * Elemental, Intermedia y Superior compartían EXACTAMENTE la misma tarjeta:
   * una banda de 130 px con el color de la materia a plena saturación. En 1ro
   * funciona; en 12mo el estudiante está mirando universidades y la página le
   * habla como si tuviera siete años.
   *
   * El código de color por materia NO se pierde: sigue ahí, pero como un filete
   * arriba y un fondo teñido al 7 %, en vez de un cartel. Ver el bloque
   * `.sala-superior` en index.css.
   */
  esSuperior?: boolean;
}

export const ROOM_CONFIG: Record<string, RoomConfig> = {
  elemental: {
    title: "Elemental",
    kicker: "1ro a 6to grado",
    subtitle: "Materias básicas con actividades paso a paso, alineadas a estándares DEPR.",
    heroRoom: "elemental",
    icon: "BookOpen",
    accent: "el",
    sectionTitle: "Materias de elemental",
    sectionMuted: "Alineado a estándares DEPR",
    esNinos: true,
  },
  intermedia: {
    title: "Intermedia",
    kicker: "7mo a 9no grado",
    subtitle: "Profundiza en las materias con retos de nivel intermedio, alineados a estándares DEPR.",
    heroRoom: "intermedia",
    icon: "Library",
    accent: "int",
    sectionTitle: "Materias de intermedia",
    sectionMuted: "Alineado a estándares DEPR",
  },
  superior: {
    title: "Superior",
    kicker: "10mo a 12mo grado",
    subtitle: "Materias de escuela superior y preparación para la universidad.",
    heroRoom: "superior",
    icon: "GraduationCap",
    accent: "sup",
    sectionTitle: "Materias de superior",
    sectionMuted: "Rumbo a la universidad",
    esSuperior: true,
  },
  stem: {
    title: "STEM y Juegos",
    kicker: "Ciencia · Tecnología · Ingeniería · Matemáticas",
    subtitle: "Robótica, programación, retos matemáticos y ciencia interactiva.",
    heroRoom: "stem",
    icon: "Puzzle",
    accent: "stem",
    sectionTitle: "Módulos STEM",
    sectionMuted: "Aprender jugando",
  },
  ia: {
    title: "Inteligencia Artificial",
    kicker: "Aprende a usar la IA con seguridad",
    subtitle: "Qué es la IA y cómo usarla para aprender, trabajar y resolver — de forma segura y ética.",
    heroRoom: "ia",
    icon: "Sparkles",
    accent: "ia",
    sectionTitle: "Cursos de IA",
    sectionMuted: "Para niños, jóvenes y adultos",
  },
};

/**
 * La clase de sala que corresponde a una ruta.
 *
 * La ponía `RoomPage` en su propio contenedor, así que la tipografía redondeada
 * de las salas de niños —y las tarjetas sobrias de superior— valían SOLO en el
 * índice de la sala. Un clic más adentro (la materia, y sobre todo el
 * reproductor de lección, que es donde el niño pasa el rato) volvía a la fuente
 * del sistema. Las dos únicas tipografías que carga la app se veían en una
 * pantalla de cada seis.
 *
 * Se resuelve desde la ruta y se aplica una vez, arriba del `Outlet`, para que
 * la herede todo lo que cuelga de la sala: `/elemental`, `/elemental/ciencias` y
 * la lección dentro.
 */
export function roomClassFor(pathname: string): string | undefined {
  const cfg = ROOM_CONFIG[pathname.split("/").filter(Boolean)[0] ?? ""];
  if (!cfg) return undefined;
  if (cfg.esNinos) return "sala-ninos";
  if (cfg.esSuperior) return "sala-superior";
  return undefined;
}

// Los enlaces de "Servicios a la Ciudadanía" se movieron a src/data/agencies.ts,
// con una página por agencia en vez de una lista plana de enlaces externos.
// (La lista vieja tenía un enlace muerto y cuatro obsoletos; ver ese archivo.)

// Categorías del Centro de Servicios al Ciudadano (radicar quejas/solicitudes).
export interface ServiceCategory {
  key: string;
  label: string;
  agency: string;
  icon: string;
  desc: string;
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  { key: "agua", label: "Agua", agency: "AAA", icon: "Droplet", desc: "Falta de servicio, salideros, baja presión o agua turbia." },
  { key: "luz", label: "Energía eléctrica", agency: "LUMA", icon: "Zap", desc: "Apagones, postes o cables caídos, alumbrado, facturación." },
  { key: "calles", label: "Calles y carreteras", agency: "DTOP / Municipio", icon: "Construction", desc: "Baches, señales, drenaje, alumbrado de vías." },
  { key: "seguridad", label: "Seguridad", agency: "Policía de PR", icon: "Shield", desc: "Situaciones de seguridad o convivencia en la comunidad." },
  { key: "servicios-sociales", label: "Servicios sociales", agency: "ADSEF / Familia", icon: "HeartHandshake", desc: "PAN, TANF, ayudas, beneficios y orientación." },
  { key: "vivienda", label: "Vivienda pública", agency: "AVP", icon: "Home", desc: "Mantenimiento, solicitudes y gestiones del residencial." },
  { key: "salud", label: "Salud", agency: "Medicaid / Salud", icon: "Stethoscope", desc: "Plan de salud, elegibilidad, citas y servicios médicos." },
  { key: "consejeria", label: "Consejería y familia", agency: "Familia", icon: "Users", desc: "Apoyo familiar, matrimonial y orientación personal." },
  { key: "general", label: "Asuntos generales", agency: "General", icon: "MessageSquare", desc: "Cualquier otra queja, sugerencia o solicitud." },
];

// Catálogo de servicios del gobierno de PR (para el ticket system de Adultos).
// modalidad: cómo se presta el servicio.
export type Modalidad = "linea" | "presencial" | "ambas";

export interface GovService {
  key: string;
  label: string;
  agency: string;
  modalidad: Modalidad;
  url?: string; // portal oficial (si aplica en línea)
  icon: string;
}

export const MODALIDAD_INFO: Record<Modalidad, { label: string; color: string }> = {
  linea: { label: "En línea", color: "var(--ok)" },
  presencial: { label: "Presencial", color: "var(--elemental)" },
  ambas: { label: "En línea y presencial", color: "var(--servicios)" },
};

export const SERVICE_CATALOG: GovService[] = [
  { key: "agua-averia", label: "Reportar avería de agua", agency: "AAA", modalidad: "ambas", url: "https://www.acueductospr.com", icon: "Droplet" },
  { key: "luz-apagon", label: "Reportar apagón o falla eléctrica", agency: "LUMA", modalidad: "linea", url: "https://www.lumapr.com", icon: "Zap" },
  { key: "cesco-licencia", label: "Licencia de conducir (renovar/duplicado)", agency: "CESCO Digital", modalidad: "ambas", url: "https://www.cescodigital.pr.gov", icon: "Car" },
  { key: "cesco-marbete", label: "Marbete (renovación de vehículo)", agency: "CESCO / DTOP", modalidad: "linea", url: "https://www.cescodigital.pr.gov", icon: "Car" },
  { key: "suri-planillas", label: "Planillas y contribuciones (SURI)", agency: "Hacienda", modalidad: "linea", url: "https://suri.hacienda.pr.gov", icon: "FileText" },
  { key: "pan-tanf", label: "PAN / TANF (asistencia nutricional)", agency: "ADSEF", modalidad: "ambas", url: "https://serviciosenlinea.adsef.pr.gov", icon: "HeartHandshake" },
  { key: "asume", label: "Pensiones alimentarias (ASUME)", agency: "ASUME", modalidad: "ambas", url: "https://servicios.asume.pr.gov", icon: "Users" },
  { key: "crim", label: "CRIM y propiedad (pagos, certificaciones)", agency: "CRIM", modalidad: "ambas", url: "https://www.crimpr.net", icon: "Building2" },
  { key: "medicaid", label: "Plan de Salud del Gobierno (Medicaid)", agency: "Medicaid PR", modalidad: "ambas", url: "https://www.medicaid.pr.gov", icon: "Stethoscope" },
  { key: "vivienda", label: "Vivienda pública (mantenimiento, solicitudes)", agency: "AVP", modalidad: "presencial", url: "https://www.avp.pr.gov", icon: "Home" },
  { key: "nacimiento", label: "Certificado de nacimiento", agency: "Registro Demográfico", modalidad: "ambas", url: "https://pr.gov", icon: "FileText" },
  { key: "penales", label: "Certificado de antecedentes penales", agency: "Policía de PR", modalidad: "ambas", url: "https://pr.gov", icon: "Shield" },
  { key: "calles", label: "Calles, baches y alumbrado", agency: "DTOP / Municipio", modalidad: "presencial", icon: "Construction" },
  { key: "seguridad", label: "Seguridad en la comunidad", agency: "Policía de PR", modalidad: "presencial", icon: "Shield" },
  { key: "consejeria", label: "Consejería y ayuda familiar", agency: "Depto. de la Familia", modalidad: "presencial", icon: "Users" },
  { key: "general", label: "Otro asunto o queja general", agency: "General", modalidad: "presencial", icon: "MessageSquare" },
];

export const TICKET_STATUS: Record<string, { label: string; color: string }> = {
  nuevo: { label: "Nuevo", color: "var(--stem)" },
  en_proceso: { label: "En proceso", color: "var(--elemental)" },
  resuelto: { label: "Resuelto", color: "var(--ok)" },
};

// (SERVICES vivía aquí; ahora es AGENCIES en src/data/agencies.ts.)
